import { StandardEvents } from '@shopify/events';

/**
 * Syncs cart attribute `rental_pay_at_pickup` when the cart contains a Verhuur/rental line.
 * Used by Payment Customization (Checkout Blocks / Function) to force pay-at-pickup.
 */

const ATTR_KEY = 'rental_pay_at_pickup';
const ATTR_VALUE = 'true';

/** @type {boolean} */
let syncing = false;

/**
 * @param {unknown} value
 * @returns {string}
 */
function asString(value) {
  if (typeof value === 'string') return value;
  if (value == null) return '';
  return String(value);
}

/**
 * @param {{ product_type?: string, sku?: string, title?: string }} item
 * @returns {boolean}
 */
function isRentalCartItem(item) {
  const productType = asString(item.product_type).toLowerCase();
  if (productType.includes('verhuur')) return true;

  const sku = asString(item.sku).toUpperCase();
  if (sku.startsWith('HUU')) return true;

  return false;
}

/**
 * @param {{ items?: Array<{ product_type?: string, sku?: string }>, attributes?: Record<string, string> }} cart
 * @returns {Promise<void>}
 */
async function syncRentalPayAtPickupAttribute(cart) {
  if (syncing) return;

  const items = Array.isArray(cart.items) ? cart.items : [];
  const hasRental = items.some((item) => isRentalCartItem(item));
  const desired = hasRental ? ATTR_VALUE : '';
  const current = asString(cart.attributes?.[ATTR_KEY]);

  if (desired === current) return;
  if (desired === '' && current === '') return;

  syncing = true;

  try {
    await fetch(Theme.routes.cart_update_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        attributes: {
          [ATTR_KEY]: desired,
        },
      }),
    });
  } catch {
    // Non-blocking: checkout rules can still use product type without this attribute.
  } finally {
    syncing = false;
  }
}

/**
 * @returns {Promise<void>}
 */
async function refreshFromCart() {
  try {
    const response = await fetch('/cart.js', {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) return;

    const cart = await response.json();
    await syncRentalPayAtPickupAttribute(cart);
  } catch {
    // Ignore network errors on initial sync.
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, () => {
  refreshFromCart();
});

refreshFromCart();
